import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useWalletTab, WalletLogTab, WalletTab } from "./use-wallet-tab";
import useWalletPub from "./use-wallet";
import {
  MemberHistoryDto,
  WalletAreaPubResponse,
  WalletPubDto,
} from "../../../../api/member";
import useWalletHistory from "./use-history";
import useOrder from "./use-order";
import { Cafe } from "../../../../api/types";

interface WalletContextType extends ReturnType<typeof useWalletTab> {
  walletLoading: boolean;
  historyloading: boolean;
  walletList: WalletAreaPubResponse[];
  seed: number;
  totalPages: number;
  historyList: MemberHistoryDto[];
  currentPage: number;
  setCurrentPage: (v: number) => void;
  selectedPub: WalletPubDto | null;
  setSelectedPub: (v: WalletPubDto | null) => void;
  pubList: WalletPubDto[];
  selectedFranchisePub: Cafe | null;
  setSelectedFranchisePub: (v: Cafe | null) => void;
}

const WalletContext = createContext<WalletContextType | null>(null);

export const WalletProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const {
    walletTab,
    setWalletTab,
    isWalletTab,
    logTab,
    setLogTab,
    isLogTab,
    getFranchisePub,
    isFranchisePubOpen,
    setIsFranchisePubOpen,
  } = useWalletTab();
  const { walletList, seed, walletLoading } = useWalletPub();
  const { selectedFranchisePub, setSelectedFranchisePub } = useOrder();
  const {
    totalPages,
    historyList,
    currentPage,
    setCurrentPage,
    historyloading,
  } = useWalletHistory();
  const [selectedPub, setSelectedPub] = useState<WalletPubDto | null>(null);

  const pubList: WalletPubDto[] = useMemo(() => {
    if (isLogTab(WalletLogTab.POINT)) {
      return walletList.flatMap((area) =>
        area.cafes.filter(
          (pub) => pub.pubType !== "RUNNER" && pub.pubType !== "VIP"
        )
      );
    }
    return walletList.flatMap((area) =>
      area.cafes.filter((pub) => pub.pubType !== "VIP")
    );
  }, [logTab]);

  useEffect(() => {
    if (isWalletTab(WalletTab.HISTORY)) setCurrentPage(1);
  }, [walletTab]);

  const value = {
    // 로딩
    walletLoading,
    historyloading,
    // 탭
    walletTab,
    setWalletTab,
    isWalletTab,
    logTab,
    setLogTab,
    isLogTab,
    // 매장 리스트
    walletList,
    seed,
    // 히스토리
    totalPages,
    historyList,
    currentPage,
    setCurrentPage,
    // 로그 펍 필터
    selectedPub,
    setSelectedPub,
    // 로그 펍 필터 리스트
    pubList,
    // 가맹펍 리스트 호출
    getFranchisePub,
    isFranchisePubOpen,
    setIsFranchisePubOpen,
    // 가맹펍 선택
    selectedFranchisePub,
    setSelectedFranchisePub,
  };

  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  );
};

export const useWalletContext = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error("useContext must be used within a WalletProvider");
  }
  return context;
};
